import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Box,
  Grid,
  List,
  Avatar,
} from '@mui/material';
import type { SparkTaskStatus } from 'types/entityTypes/SparkTaskStatus';
import { showRunsSparkStatus } from 'components/Dashboard/HelperFunctions/showRecentSparkStatuses';
import { formatDateAndTime } from 'utilities/time';
import type { JoinedRunTasks } from 'hooks/SparkRuns/useSparkRunTaskByRunID';
import { formatCredits } from 'utilities/index';

type SparkTaskAccordionProps = {
  joinedRunTask: JoinedRunTasks | undefined;
  sparkTaskIdMap: Record<string, SparkTaskStatus> | undefined;
  children: React.ReactNode;
};

function SparkTaskAccordion({
  joinedRunTask,
  sparkTaskIdMap,
  children,
}: SparkTaskAccordionProps) {
  const taskAttributes = joinedRunTask?.attributes;
  const statusIconUrl =
    sparkTaskIdMap?.[taskAttributes?.sparkTaskStatusTypeID || ''].attributes
      .name;
  const credits = formatCredits(joinedRunTask?.meta.credits);
  return (
    <Accordion
      slotProps={{ transition: { unmountOnExit: true } }}
      style={{ marginTop: 0 }}
      sx={{
        width: '100%',
        '& .MuiAccordionDetails-root': { padding: 0 },
        '&::before': { display: 'none' },
        borderRadius: '10px !important',
      }}
      disableGutters={true}
      variant='outlined'
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`spark-accordion-header`}
        id={`spark-accordion-header`}
      >
        <Box width='100%' sx={{ borderRadius: '10px' }}>
          <List>
            <Grid display='flex' justifyContent='center' alignItems='center'>
              <Box display='flex' alignItems='center'>
                {showRunsSparkStatus(statusIconUrl, {
                  width: '28',
                  height: '28',
                })}
              </Box>

              <Box flex={1} display='flex' gap={2}>
                <Box display='flex' alignItems='center'>
                  <Avatar
                    sx={{ width: '28px', height: '28px' }}
                    src={joinedRunTask?.stepType?.attributes.iconURL}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      color: '#000000',
                    }}
                  >
                    {joinedRunTask?.sparkStep?.attributes.stepOrder +
                      '. ' +
                      joinedRunTask?.stepType?.attributes.name}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#BDBDBD',
                      fontSize: '14px',
                      pt: 0.5,
                    }}
                  >
                    {formatDateAndTime(joinedRunTask?.attributes.createdAt)}
                  </Typography>
                </Box>
              </Box>

              <Box
                display='flex'
                flexWrap='wrap'
                alignItems='center'
                gap={1}
                sx={{
                  color: '#000000',
                  fontSize: '14px',
                  textAlign: 'center',
                }}
              >
                <Typography>{credits}</Typography>
                <Typography>{credits !== 1 ? 'Credits' : 'Credit'}</Typography>
              </Box>
            </Grid>
          </List>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container>{children}</Grid>
      </AccordionDetails>
    </Accordion>
  );
}

export default SparkTaskAccordion;
